/**
 * Ввод value для строки card_field_rules: по properties из value_schema (JSON Schema) или сырой JSON.
 */

import { useEffect, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface RuleValueEditorProps {
  valueKind: string;
  valueSchema: Record<string, unknown> | null | undefined;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
}

function isSchemaProperties(
  schema: Record<string, unknown> | null | undefined
): schema is { properties: Record<string, unknown> } {
  if (!schema || typeof schema !== "object") return false;
  const p = schema.properties;
  return typeof p === "object" && p !== null && !Array.isArray(p) && Object.keys(p).length > 0;
}

function SchemaPropertiesForm({
  properties,
  value,
  onChange,
  disabled,
}: {
  properties: Record<string, unknown>;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
}) {
  const patch = (key: string, v: unknown) => {
    onChange({ ...value, [key]: v });
  };

  return (
    <div className="space-y-2 pl-1">
      {Object.entries(properties).map(([key, sub]) => {
        const subObj = sub as { type?: string; description?: string };
        const t = subObj.type;
        const current = value[key];
        if (t === "boolean") {
          return (
            <div key={key} className="flex items-center gap-2">
              <Checkbox
                id={`rv-${key}`}
                checked={Boolean(current)}
                disabled={disabled}
                onCheckedChange={(c) => patch(key, c === true)}
              />
              <Label htmlFor={`rv-${key}`} className="text-xs font-normal cursor-pointer">
                {key}
                {subObj.description ? (
                  <span className="text-muted-foreground ml-1">({subObj.description})</span>
                ) : null}
              </Label>
            </div>
          );
        }
        if (t === "number" || t === "integer") {
          return (
            <div key={key} className="space-y-1">
              <Label className="text-xs">{key}</Label>
              <Input
                type="number"
                className="h-8 text-xs"
                disabled={disabled}
                value={current === undefined || current === null ? "" : String(current)}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    const { [key]: _removed, ...rest } = value;
                    onChange(rest);
                    return;
                  }
                  const n = Number(raw);
                  patch(key, Number.isFinite(n) ? n : raw);
                }}
              />
            </div>
          );
        }
        if (t === "string") {
          return (
            <div key={key} className="space-y-1">
              <Label className="text-xs">{key}</Label>
              <Input
                className="h-8 text-xs font-mono"
                disabled={disabled}
                value={current != null ? String(current) : ""}
                onChange={(e) => patch(key, e.target.value)}
              />
            </div>
          );
        }
        if (t === "array" || t === "object") {
          return (
            <div key={key} className="space-y-1">
              <Label className="text-xs">{key} (JSON)</Label>
              <Textarea
                className="font-mono text-xs min-h-[72px]"
                disabled={disabled}
                value={current === undefined ? "" : JSON.stringify(current, null, 2)}
                onChange={(e) => {
                  const raw = e.target.value.trim();
                  if (!raw) {
                    const { [key]: _removed, ...rest } = value;
                    onChange(rest);
                    return;
                  }
                  try {
                    patch(key, JSON.parse(raw));
                  } catch {
                    /* ошибка валидации — сервер */
                  }
                }}
              />
            </div>
          );
        }
        return (
          <div key={key} className="text-xs text-muted-foreground">
            {key}: неподдерживаемый тип в схеме ({String(t)})
          </div>
        );
      })}
    </div>
  );
}

function JsonFallback({
  value,
  onChange,
  disabled,
}: {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setText(JSON.stringify(value, null, 2));
    setErr(null);
  }, [value]);

  return (
    <div className="space-y-1">
      <Textarea
        className="font-mono text-xs min-h-[120px]"
        disabled={disabled}
        value={text}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          try {
            const parsed = JSON.parse(t) as unknown;
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
              onChange(parsed as Record<string, unknown>);
              setErr(null);
            } else {
              setErr("Корень JSON должен быть объектом");
            }
          } catch {
            setErr("Невалидный JSON");
          }
        }}
      />
      {err ? <p className="text-xs text-destructive">{err}</p> : null}
    </div>
  );
}

export function RuleValueEditor({
  valueKind: _valueKind,
  valueSchema,
  value,
  onChange,
  disabled,
}: RuleValueEditorProps) {
  if (isSchemaProperties(valueSchema)) {
    return (
      <SchemaPropertiesForm
        properties={valueSchema.properties}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  return <JsonFallback value={value} onChange={onChange} disabled={disabled} />;
}
